import { useEffect, useState } from "react";
import axios from "axios";
import {
  Stack,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Button,
  Link,
  TextField,
  MenuItem,
  Checkbox,
  ListItemText,
  Box,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonnelDialog from "../components/PersonnelDialog";
import { API_BASE_URL } from "../config";
import { useMe } from "../MeContext";

const API = `${API_BASE_URL}/compliance`;

const CATEGORY_COLOR = {
  "Qualification certificate": "warning",
  "Indemnity": "error",
  "ID check": "info",
  "General info": "secondary",
  "Contract": "primary",
};

// Plain-English description of each non-compliance category the check looks for.
const CATEGORY_DESCRIPTIONS = [
  {
    category: "Qualification certificate",
    text: "Flags a certificate the person's role on the event needs but which isn't uploaded, " +
      "or which expires before the event ends. Response Team members with RRMT ticked must have an " +
      "unexpired RRMT certificate (without RRMT ticked, nothing is asked). Medical Team, unless a " +
      "healthcare professional, needs one pre-hospital care certificate (RRMT, FREC4 or WEMT). " +
      "Mountain Safety needs a First Aid certificate if First Aid is ticked, and " +
      "one mountain qualification (ML, WML, MCI or WMCI), each flagged separately. Where several " +
      "would do, only one valid certificate is needed.",
  },
  {
    category: "Indemnity",
    text: "For healthcare professionals (Doctor, Doctor in training, Physician Associate, Nurse, Paramedic, Physiotherapist), " +
      "flags a missing indemnity provider, no indemnity certificate uploaded, or indemnity that expires before the event ends.",
  },
  {
    category: "ID check",
    text: "For healthcare professionals, flags a professional registration number (GMC, NMC or HCPC) " +
      "that is not recorded at all, one that has never been checked, or one that was last checked " +
      "more than a year before the event starts.",
  },
  {
    category: "General info",
    text: "For everyone except a Race Organiser, flags missing roster basics: emergency contact " +
      "name or phone, clothing size, home address (line 1, town and postcode), and no CV uploaded " +
      "(not asked of anyone marked \"CV not required\").",
  },
  {
    category: "Contract",
    text: "Flags an attendance whose contract is not signed — status Selected or Contract Sent.",
  },
];

export default function Compliance() {
  const { can } = useMe();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nameFilter, setNameFilter] = useState("");
  const [eventFilter, setEventFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  // Personnel record open over this page; closing it re-runs the check, so a
  // non-compliance just fixed disappears from the table.
  const [editing, setEditing] = useState(null);
  const canOpenRecord = can("Personnel.viewAll");

  const load = () => {
    setLoading(true);
    axios.get(API + "/").then(r => setIssues(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // A multi-select chip filter. `options` are {value, label}; selecting none = no filter.
  const filterSelect = (label, value, setValue, options) => (
    <TextField
      label={label} select size="small" sx={{ minWidth: 200 }}
      value={value}
      onChange={e => {
        const v = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
        setValue(v);
      }}
      slotProps={{
        inputLabel: { shrink: true },
        select: {
          multiple: true,
          displayEmpty: true,
          renderValue: (selected) => (
            selected.length === 0
              ? <Typography variant="body2" color="text.secondary">Any</Typography>
              : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map(v => {
                    const o = options.find(x => x.value === v);
                    return <Chip key={v} label={o ? o.label : v} size="small" />;
                  })}
                </Box>
              )
          ),
        },
      }}
    >
      {options.length === 0 && <MenuItem disabled><em>None</em></MenuItem>}
      {options.map(o => (
        <MenuItem key={o.value} value={o.value}>
          <Checkbox checked={value.indexOf(o.value) > -1} />
          <ListItemText primary={o.label} />
        </MenuItem>
      ))}
    </TextField>
  );

  // Filter options derived from the loaded issues.
  const eventOptions = Array.from(
    new Map(issues.map(i => [i.event_id, i.event_label])).entries()
  ).map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const typeOptions = Array.from(new Set(issues.map(i => i.category)))
    .sort()
    .map(c => ({ value: c, label: c }));

  const name = nameFilter.trim().toLowerCase();
  const filtered = issues.filter(i =>
    (name === "" || i.person_name.toLowerCase().includes(name)) &&
    (eventFilter.length === 0 || eventFilter.includes(i.event_id)) &&
    (typeFilter.length === 0 || typeFilter.includes(i.category))
  );

  // Upcoming events first, then by person.
  const sorted = [...filtered].sort((a, b) =>
    (a.event_start || "").localeCompare(b.event_start || "") ||
    a.person_name.localeCompare(b.person_name));

  const peopleCount = new Set(filtered.map(i => i.personnel_id)).size;

  const anyFilterActive = nameFilter !== "" || eventFilter.length > 0 || typeFilter.length > 0;
  const clearFilters = () => { setNameFilter(""); setEventFilter([]); setTypeFilter([]); };

  return (
    <Stack spacing={3}>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h4">Compliance</Typography>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
      </Stack>

      {/* Filter */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <TextField label="Filter by name" size="small" value={nameFilter}
                   onChange={e => setNameFilter(e.target.value)} />
        {filterSelect("Event", eventFilter, setEventFilter, eventOptions)}
        {filterSelect("Type", typeFilter, setTypeFilter, typeOptions)}
        {anyFilterActive && (
          <Button size="small" onClick={clearFilters}>Clear filters</Button>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: "auto" } }}>
          {filtered.length} of {issues.length}
        </Typography>
      </Stack>

      <Typography variant="body2" color="text.secondary">
        {filtered.length} non-compliance{filtered.length === 1 ? "" : "s"} across {peopleCount}{" "}
        {peopleCount === 1 ? "person" : "people"} linked to upcoming events.
      </Typography>

      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                <TableCell>Person</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Issue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((i, idx) => (
                <TableRow key={idx} hover>
                  <TableCell>
                    {canOpenRecord ? (
                      <Link component="button" type="button" underline="hover" sx={{ textAlign: "left" }}
                            onClick={() => setEditing(i.personnel_id)}>
                        {i.person_name}
                      </Link>
                    ) : i.person_name}
                  </TableCell>
                  <TableCell>
                    {i.event_label}
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {i.event_start}{i.event_end && i.event_end !== i.event_start ? ` → ${i.event_end}` : ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={i.category} color={CATEGORY_COLOR[i.category] || "default"} />
                  </TableCell>
                  <TableCell>{i.detail}</TableCell>
                </TableRow>
              ))}
              {sorted.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ color: "text.secondary", py: 4 }}>
                    {loading ? "Checking…"
                      : issues.length === 0 ? "No non-compliances 🎉"
                      : "No non-compliances match your filters."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* What the compliance check looks for */}
      <Stack spacing={1}>
        <Typography variant="h6">What is checked</Typography>
        <Typography variant="body2" color="text.secondary">
          Only people on an event that has not yet finished are checked, and only where their
          attendance status is Selected, Contract Sent or Contract Signed — Available and No
          Contract Needed are skipped entirely. The following non-compliances are flagged:
        </Typography>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          {CATEGORY_DESCRIPTIONS.map(d => (
            <Box key={d.category} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
              <Chip size="small" label={d.category} color={CATEGORY_COLOR[d.category] || "default"}
                    sx={{ mt: 0.25, flexShrink: 0 }} />
              <Typography variant="body2">{d.text}</Typography>
            </Box>
          ))}
        </Stack>
      </Stack>

      {/* The person's record, over this page — shared with the Personnel roster.
          Re-check on close, since the gap may now be filled. */}
      <PersonnelDialog
        personId={editing}
        onClose={() => { setEditing(null); load(); }} />
    </Stack>
  );
}
